import { Action } from '../stores/types/types';

export enum SolutionCenterDrawerStates {
	Closed = 'closed',
	Collapsed = 'collapsed',
	Open = 'open',
}

export enum SolutionCenterViews {
	Home = 'home',
	Search = 'search',
	Category = 'category',
	Section = 'section',
	Content = 'content',
}

export enum SolutionCenterContentTypes {
	Article = 'article',
	Post = 'post',
}

export enum RecentActivityTypes {
	Article = 'article',
	Post = 'post',
	Category = 'category',
}

export enum ZendeskSortBy {
	Position = 'position',
	CreatedAt = 'created_at',
	UpdatedAt = 'updated_at',
}

export enum ZendeskSortOrder {
	Ascending = 'asc',
	Descending = 'desc',
}

export interface SolutionCenterContent {
	id: number;
	type: SolutionCenterContentTypes;
}

export interface SolutionCenterDrawerStateAction extends Action {
	payload: {
		drawer: SolutionCenterDrawerStates;
	}
}

export interface SolutionCenterContentStateAction extends Action {
	payload: {
		content: SolutionCenterContent | null;
		view?: SolutionCenterViews;
	}
}

export interface RecentActivityItem {
	id: number;
	title: string;
	snippet: string;
	url: string;
	type: RecentActivityTypes;
}

export interface ZendeskCategory {
	created_at: string;
	description: string;
	html_url: string;
	id: number;
	locale: string;
	name: string;
	outdated: boolean;
	position: number;
	source_locale: string;
	updated_at: string;
	url: string;
}

export interface ZendeskSection {
	category_id: number;
	created_at: string;
	description: string;
	html_url: string;
	id: number;
	locale: string;
	name: string;
	outdated: boolean;
	parent_section_id: number;
	position: number;
	source_locale: string;
	theme_template: string;
	updated_at: string;
	url: string;
}

export interface ZendeskArticle {
	author_id: number;
	body: string;
	comments_disabled: boolean;
	content_tag_ids: number[];
	created_at: string;
	draft: boolean;
	edited_at: string;
	html_url: string;
	id: number;
	label_names: string[];
	locale: string;
	outdated: boolean;
	outdated_locales: string[];
	permission_group_id: number;
	position: number;
	promoted: boolean;
	section_id: number;
	source_locale: string;
	title: string;
	updated_at: string;
	url: string;
	user_segment_id?: number | null;
	user_segment_ids?: number[];
	vote_count: number;
	vote_sum: number;
}

export interface ZendeskArticleAttachment {
	article_id: number;
	content_type: string;
	content_url: string;
	created_at: string;
	file: File | null;
	file_name: string;
	guide_media_id: string | null;
	id: number;
	inline: boolean;
	locale: string | null;
	size: number;
	updated_at: string;
	url: string;
}

export interface ZendeskPost {
	author_id: number;
	closed: boolean;
	comment_count: number;
	content_tag_ids: number[];
	created_at: string;
	details: string;
	featured: boolean;
	follower_count: number;
	html_url: string;
	id: number;
	non_author_editor_id: number | null;
	non_author_updated_at: string | null;
	pinned: boolean;
	status: string;
	title: string;
	topic_id: number;
	updated_at: string;
	url: string;
	vote_count: number;
	vote_sum: number;
}

export interface ZendeskSearchArticle extends ZendeskArticle {
	result_type: string;
	snippet: string;
	category_id: number;
	category_name: string;
	section_name: string;
}

export interface GetZendeskArticleDTO {
	article: ZendeskArticle;
}

export interface GetZendeskArticleAttachmentsDTO {
	article_attachments: ZendeskArticleAttachment[];
}

export interface GetZendeskPostDTO {
	post: ZendeskPost;
}

export interface GetZendeskCategoriesDTO {
	categories: ZendeskCategory[];
	page: number;
	previous_page: number | null;
	next_page: number | null;
	per_page: number;
	page_count: number;
	count: number;
	sort_by: ZendeskSortBy;
	sort_order: ZendeskSortOrder;
}

export interface GetZendeskSectionsDTO {
	sections: ZendeskSection[];
	page: number;
	previous_page: number | null;
	next_page: number | null;
	per_page: number;
	page_count: number;
	count: number;
	sort_by: ZendeskSortBy;
	sort_order: ZendeskSortOrder;
}

export interface GetZendeskArticlesDTO {
	count: number;
	next_page: string | null;
	page: number;
	page_count: number;
	per_page: number;
	previous_page: string | null;
	articles: ZendeskArticle[];
	sort_by: ZendeskSortBy;
	sort_order: ZendeskSortOrder;
}

export interface SearchZendeskArticlesDTO {
	count: number;
	next_page: string | null;
	page: number;
	page_count: number;
	per_page: number;
	previous_page: string | null;
	results: ZendeskSearchArticle[];
}

export enum SolutionCenterArticles {
	FeatureOverviewLocations = 35780874897179,
	FeatureOverviewSRTConnection = 40209141379355,
	PlatformNotificationsServiceAdvisories = 360056772612,
	FollowContentAndManageYourArticleSubscriptions = 360026188851,
	WelcomeToTheBrandlivePlatform = 30994372879131,
};

export enum SolutionCenterPosts {
	ScheduledMaintenance = 205053156,
};

export enum SolutionCenterCategories {
	BrandliveResources = 30603861176219,
};

export const RECENT_ACTIVITY_ITEMS: RecentActivityItem[] = [
	{
		id: SolutionCenterArticles.PlatformNotificationsServiceAdvisories,
		title: 'Platform Notifications / Service Advisories',
		snippet: 'This page provides status information on all Brandlive Platforms.',
		url: 'https://support.brandlive.com/hc/en-us/articles/360056772612-There-are-no-Service-Advisories-at-this-time',
		type: RecentActivityTypes.Article,
	},
	{
		id: SolutionCenterPosts.ScheduledMaintenance,
		title: 'Scheduled Maintenance',
		snippet: 'This space will be used to push maintenance notification alerts to subscribers.',
		url: 'https://support.brandlive.com/hc/en-us/community/posts/205053156',
		type: RecentActivityTypes.Post,
	},
	{
		id: SolutionCenterArticles.FollowContentAndManageYourArticleSubscriptions,
		title: 'Follow content and manage your article subscriptions',
		snippet: 'Learn how to follow sections and articles in the knowledge base.',
		url: 'https://support.brandlive.com/hc/en-us/articles/360026188851-Follow-content-and-manage-your-article-subscriptions',
		type: RecentActivityTypes.Article,
	},
	{
		id: SolutionCenterArticles.WelcomeToTheBrandlivePlatform,
		title: 'Welcome to the Brandlive Platform',
		snippet: 'Get started on creating your Brandlive experience.',
		url: 'https://support.brandlive.com/hc/en-us/articles/30994372879131-Welcome-to-the-Brandlive-Platform',
		type: RecentActivityTypes.Article,
	},
	{
		id: SolutionCenterCategories.BrandliveResources,
		title: 'Brandlive Resources',
		snippet: 'Additional support information for Brandlive events.',
		url: 'https://support.brandlive.com/hc/en-us/categories/30603861176219-Brandlive-Resources',
		type: RecentActivityTypes.Category,
	},
];
